import React, { useState, useEffect, useRef } from 'react';
import { useTheme } from '../context/ThemeContext';

export default function Header({ setView, user, setUser, activeTab, setActiveTab }) {
  const { tenantConfig } = useTheme();
  const [showMenu, setShowMenu] = useState(false);
  const menuRef = useRef(null);
  
  // Track viewport responsiveness dynamically
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Close dropdown on click outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setShowMenu(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const [showTokenModal, setShowTokenModal] = useState(false);
  const [tokenCopied, setTokenCopied] = useState(false);

  // Close token modal on Escape key
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape' && showTokenModal) {
        setShowTokenModal(false);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [showTokenModal]);

  const copyTextToClipboard = (text) => {
    if (!text) return;
    if (navigator?.clipboard?.writeText) {
      navigator.clipboard.writeText(text).catch(() => {
        fallbackCopyText(text);
      });
    } else {
      fallbackCopyText(text);
    }
    setTokenCopied(true);
    setTimeout(() => setTokenCopied(false), 2500);
  };

  const fallbackCopyText = (text) => {
    try {
      const el = document.createElement('textarea');
      el.value = text;
      el.setAttribute('readonly', '');
      el.style.position = 'absolute';
      el.style.left = '-9999px';
      document.body.appendChild(el);
      el.select();
      document.execCommand('copy');
      document.body.removeChild(el);
    } catch (err) {
      console.error('Fallback copy failed:', err);
    }
  };

  if (!tenantConfig) return null;

  const handleSignOut = () => {
    sessionStorage.removeItem('token');
    localStorage.removeItem('token');
    setUser(null);
    setView('landing');
  };

  const headerPadding = isMobile ? '12px 15px' : '15px 40px';
  const navGap = isMobile ? '12px' : '24px';
  const hasSettingsAccess = user && ["GLOBAL_ADMIN", "GLOBAL_POWER", "TENANT_ADMIN"].includes(user.role);
  const canMint = user && user.role !== 'END_USER';

  const logoOffsetY = Number(tenantConfig.logo_offset_y ?? 0);
  const logoOffsetX = Number(tenantConfig.logo_offset_x ?? 0);
  const logoHeight = Number(tenantConfig.logo_height || 40);
  const bannerHeight = Number(tenantConfig.banner_height || 70);

  const activeOffsetY = isMobile ? Math.round(logoOffsetY * 0.75) : logoOffsetY;
  const activeOffsetX = isMobile ? Math.round(logoOffsetX * 0.75) : logoOffsetX;
  const activeHeight = isMobile ? Math.round(logoHeight * 0.75) : logoHeight;
  const activeBannerHeight = isMobile ? Math.max(50, Math.round(bannerHeight * 0.85)) : bannerHeight;

  return (
    <>
      <style>{`
        @keyframes slideDownFade {
          from {
            opacity: 0;
            transform: translateY(-8px) scale(0.95);
          }
          to {
            opacity: 1;
            transform: translateY(0) scale(1);
          }
        }
      `}</style>
      <header style={{
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      padding: headerPadding,
      minHeight: `${activeBannerHeight}px`,
      boxSizing: 'border-box',
      background: 'var(--header-bg)',
      borderBottom: '1px solid var(--border-color)',
      transition: 'background-color 0.3s ease, border-color 0.3s ease',
      flexWrap: isMobile ? 'wrap' : 'nowrap',
      gap: isMobile ? '10px' : '0',
      position: 'sticky',
      top: 0,
      flexShrink: 0,
      zIndex: 100
    }}>
      {/* Brand Section */}
      <div 
        onClick={() => {
          setView('landing');
          setActiveTab('overview');
        }}
        style={{ display: 'flex', alignItems: 'center', gap: '12px', cursor: 'pointer' }}
      >
        {tenantConfig.logo_url ? (
          <img 
            src={tenantConfig.logo_url} 
            alt={`${tenantConfig.brand_name} Logo`} 
            style={{ 
              maxHeight: `${activeHeight}px`, 
              maxWidth: isMobile ? '220px' : '290px', 
              objectFit: 'contain',
              position: (activeOffsetY !== 0 || activeOffsetX !== 0) ? 'relative' : 'static',
              top: activeOffsetY ? `${activeOffsetY}px` : undefined,
              left: activeOffsetX ? `${activeOffsetX}px` : undefined,
              marginBottom: activeOffsetY > 0 ? `-${activeOffsetY}px` : (activeOffsetY < 0 ? `${Math.abs(activeOffsetY)}px` : undefined),
              zIndex: 110
            }}
          />
        ) : (
          <span style={{ fontSize: isMobile ? '18px' : '22px' }}>🏆</span>
        )}
        <span style={{
          fontSize: isMobile ? '16px' : '20px',
          fontWeight: '600',
          color: 'var(--header-text)',
          letterSpacing: '-0.5px'
        }}>
          {tenantConfig.brand_name}
        </span>
      </div>

      {/* Nav List & Actions */}
      <nav style={{
        display: 'flex',
        alignItems: 'center',
        gap: navGap
      }}>
        {/* Hide extra nav items on small mobile screen to optimize fit */}
        {!isMobile && (
          <ul style={{
            display: 'flex',
            listStyle: 'none',
            gap: '20px',
            margin: 0,
            padding: 0
          }}>
            <li>
              <a 
                href="#home" 
                onClick={(e) => {
                  e.preventDefault();
                  setView('landing');
                  setActiveTab('overview');
                }} 
                style={{ 
                  color: 'var(--header-text)', 
                  textDecoration: 'none', 
                  fontSize: '13px',
                  fontWeight: activeTab === 'overview' ? '600' : '400',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '5px'
                }}
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
                  <polyline points="9 22 9 12 15 12 15 22" />
                </svg>
                Home
              </a>
            </li>
            {canMint && (
              <li>
                <a 
                  href="#quick-mint" 
                  onClick={(e) => {
                    e.preventDefault();
                    setView('landing');
                    setActiveTab('mint');
                  }} 
                  style={{ 
                    color: 'var(--header-text)', 
                    textDecoration: 'none', 
                    fontSize: '13px',
                    fontWeight: activeTab === 'mint' ? '600' : '400',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '5px'
                  }}
                >
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="12" cy="12" r="10" />
                    <path d="M12 6v12M12 10a2 2 0 0 0-2 2 2 2 0 0 0 2 2h2a2 2 0 0 1 2 2 2 2 0 0 1-2 2m-2-12a2 2 0 0 1 2-2h2" />
                  </svg>
                  Quick Mint
                </a>
              </li>
            )}
            <li>
              <a 
                href="#support" 
                data-testid="header-support-link"
                onClick={(e) => {
                  e.preventDefault();
                  setView('landing');
                  setActiveTab('support');
                }} 
                style={{ 
                  color: 'var(--header-text)', 
                  textDecoration: 'none', 
                  fontSize: '13px',
                  fontWeight: activeTab === 'support' ? '600' : '400',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '5px'
                }}
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="10" />
                  <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3" />
                  <line x1="12" y1="17" x2="12.01" y2="17" />
                </svg>
                Support
              </a>
            </li>
          </ul>
        )}

        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          {user ? (
            <div style={{ position: 'relative' }} ref={menuRef}>
              
              {/* Profile Avatar Icon */}
              <button 
                data-testid="user-profile-menu-btn"
                onClick={() => setShowMenu(!showMenu)}
                style={{
                  width: '34px',
                  height: '34px',
                  borderRadius: '50%',
                  backgroundColor: 'var(--accent-color)',
                  color: 'var(--bg-color)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontWeight: '700',
                  fontSize: '14px',
                  cursor: 'pointer',
                  border: '1px solid var(--border-color)',
                  outline: 'none',
                  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
                  transition: 'transform 0.2s ease',
                  padding: 0
                }}
                onMouseOver={(e) => e.currentTarget.style.transform = 'scale(1.05)'}
                onMouseOut={(e) => e.currentTarget.style.transform = 'scale(1)'}
                title="Account Settings"
              >
                {user.email.charAt(0).toUpperCase()}
              </button>

              {/* Floating Dropdown Menu */}
              {showMenu && (
                <div style={{
                  position: 'absolute',
                  top: '42px',
                  right: '0',
                  background: 'var(--panel-bg)',
                  border: '1px solid var(--border-color)',
                  borderRadius: '8px',
                  boxShadow: 'var(--card-shadow)',
                  padding: '12px',
                  minWidth: '200px',
                  zIndex: 1000,
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '8px',
                  boxSizing: 'border-box',
                  transformOrigin: 'top right',
                  animation: 'slideDownFade 0.2s cubic-bezier(0.16, 1, 0.3, 1) forwards'
                }}>
                  <div style={{
                    borderBottom: '1px solid var(--border-color)',
                    paddingBottom: '8px',
                    marginBottom: '4px',
                    fontSize: '12px',
                    color: 'var(--text-mute)',
                    lineHeight: '1.4'
                  }}>
                    <div style={{ fontWeight: '700', color: 'var(--text-main)', wordBreak: 'break-all' }}>
                      {user.email}
                    </div>
                    <div style={{ fontSize: '11px', marginTop: '3px', color: 'var(--primary-color)', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.3px' }}>
                      Role: {user.role.replace('_', ' ')}
                    </div>
                  </div>

                  <button
                    onClick={() => {
                      setView('landing'); // Navigate to dashboard view
                      setActiveTab('profile'); // Open profile settings tab
                      setShowMenu(false);
                    }}
                    style={{
                      backgroundColor: 'transparent',
                      border: 'none',
                      color: 'var(--text-main)',
                      textAlign: 'left',
                      padding: '8px 6px',
                      borderRadius: '4px',
                      fontSize: '13px',
                      fontWeight: '500',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      transition: 'background 0.2s'
                    }}
                    onMouseOver={(e) => {
                      e.currentTarget.style.backgroundColor = 'var(--bg-color)';
                      e.currentTarget.style.color = 'var(--primary-color)';
                    }}
                    onMouseOut={(e) => {
                      e.currentTarget.style.backgroundColor = 'transparent';
                      e.currentTarget.style.color = 'var(--text-main)';
                    }}
                  >
                    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
                      <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                      <circle cx="12" cy="7" r="4" />
                    </svg>
                    My Profile & Account
                  </button>

                  <button
                    onClick={() => {
                      setView('landing');
                      setActiveTab('support');
                      setShowMenu(false);
                    }}
                    style={{
                      backgroundColor: 'transparent',
                      border: 'none',
                      color: 'var(--text-main)',
                      textAlign: 'left',
                      padding: '8px 6px',
                      borderRadius: '4px',
                      fontSize: '13px',
                      fontWeight: '500',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      transition: 'background 0.2s'
                    }}
                    onMouseOver={(e) => {
                      e.currentTarget.style.backgroundColor = 'var(--bg-color)';
                      e.currentTarget.style.color = 'var(--primary-color)';
                    }}
                    onMouseOut={(e) => {
                      e.currentTarget.style.backgroundColor = 'transparent';
                      e.currentTarget.style.color = 'var(--text-main)';
                    }}
                  >
                    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
                      <circle cx="12" cy="12" r="10" />
                      <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3" />
                      <line x1="12" y1="17" x2="12.01" y2="17" />
                    </svg>
                    Support & Help
                  </button>

                  {hasSettingsAccess && (
                    <button
                      onClick={() => {
                        setView('landing');
                        setActiveTab('settings');
                        setShowMenu(false);
                      }}
                      style={{
                        backgroundColor: 'transparent',
                        border: 'none',
                        color: 'var(--text-main)',
                        textAlign: 'left',
                        padding: '8px 6px',
                        borderRadius: '4px',
                        fontSize: '13px',
                        fontWeight: '500',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        transition: 'background 0.2s'
                      }}
                      onMouseOver={(e) => {
                        e.currentTarget.style.backgroundColor = 'var(--bg-color)';
                        e.currentTarget.style.color = 'var(--primary-color)';
                      }}
                      onMouseOut={(e) => {
                        e.currentTarget.style.backgroundColor = 'transparent';
                        e.currentTarget.style.color = 'var(--text-main)';
                      }}
                    >
                      <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
                        <circle cx="12" cy="12" r="3" />
                        <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" />
                      </svg>
                      Site Settings
                    </button>
                  )}

                  {hasSettingsAccess && (
                    <button
                      data-testid="get-bearer-token-btn"
                      onClick={() => {
                        const activeToken = sessionStorage.getItem('token') || localStorage.getItem('token') || '';
                        copyTextToClipboard(activeToken);
                        setShowMenu(false);
                        setShowTokenModal(true);
                      }}
                      style={{
                        backgroundColor: 'transparent',
                        border: 'none',
                        color: 'var(--text-main)',
                        textAlign: 'left',
                        padding: '8px 6px',
                        borderRadius: '4px',
                        fontSize: '13px',
                        fontWeight: '500',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        transition: 'background 0.2s'
                      }}
                      onMouseOver={(e) => {
                        e.currentTarget.style.backgroundColor = 'var(--bg-color)';
                        e.currentTarget.style.color = 'var(--primary-color)';
                      }}
                      onMouseOut={(e) => {
                        e.currentTarget.style.backgroundColor = 'transparent';
                        e.currentTarget.style.color = 'var(--text-main)';
                      }}
                    >
                      <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
                        <circle cx="7.5" cy="15.5" r="4.5" />
                        <path d="M10.7 12.3L19 4" />
                        <path d="M15.5 4.5l3 3" />
                        <path d="M13 7l2 2" />
                      </svg>
                      Get Bearer Token
                    </button>
                  )}

                  <button
                    onClick={() => {
                      handleSignOut();
                      setShowMenu(false);
                    }}
                    style={{
                      backgroundColor: 'transparent',
                      border: 'none',
                      color: '#ff7b72',
                      textAlign: 'left',
                      padding: '8px 6px',
                      borderRadius: '4px',
                      fontSize: '13px',
                      fontWeight: '600',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      transition: 'background 0.2s'
                    }}
                    onMouseOver={(e) => {
                      e.currentTarget.style.backgroundColor = 'var(--bg-color)';
                    }}
                    onMouseOut={(e) => {
                      e.currentTarget.style.backgroundColor = 'transparent';
                    }}
                  >
                    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
                      <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
                      <polyline points="16 17 21 12 16 7" />
                      <line x1="21" y1="12" x2="9" y2="12" />
                    </svg>
                    Sign Out
                  </button>
                </div>
              )}
            </div>
          ) : (
            <button 
              onClick={() => setView('auth')}
              style={{
                backgroundColor: 'var(--primary-color)',
                color: 'white',
                border: 'none',
                borderRadius: '6px',
                padding: '6px 16px',
                fontSize: '12px',
                fontWeight: '600',
                cursor: 'pointer',
                boxShadow: '0 2px 8px var(--accent-color)'
              }}
            >
              {tenantConfig.allow_public_signup ? 'Register / Sign In' : 'Sign In'}
            </button>
          )}
        </div>
      </nav>
    </header>

    {/* Admin Bearer Token Modal */}
    {showTokenModal && (
      <div
        data-testid="bearer-token-modal"
        onClick={() => setShowTokenModal(false)}
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.75)',
          backdropFilter: 'blur(4px)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 100000,
          padding: '20px'
        }}
      >
        <div
          onClick={(e) => e.stopPropagation()}
          style={{
            backgroundColor: '#18181b',
            border: '1px solid #27272a',
            borderRadius: '12px',
            maxWidth: '540px',
            width: '100%',
            boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.7)',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
            color: '#f4f4f5'
          }}
        >
          {/* Modal Header */}
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: '16px 20px',
            borderBottom: '1px solid #27272a'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <div style={{
                backgroundColor: 'rgba(96, 165, 250, 0.15)',
                color: '#60a5fa',
                borderRadius: '8px',
                width: '32px',
                height: '32px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="7.5" cy="15.5" r="4.5" />
                  <path d="M10.7 12.3L19 4" />
                  <path d="M15.5 4.5l3 3" />
                  <path d="M13 7l2 2" />
                </svg>
              </div>
              <div>
                <h3 style={{ margin: 0, fontSize: '15px', fontWeight: '600', color: '#ffffff' }}>
                  Admin Bearer Token
                </h3>
                <p style={{ margin: 0, fontSize: '11px', color: '#a1a1aa' }}>
                  Active session token for API calls and Remote Push
                </p>
              </div>
            </div>

            <button
              data-testid="close-bearer-token-modal-btn"
              onClick={() => setShowTokenModal(false)}
              style={{
                background: 'transparent',
                border: 'none',
                color: '#a1a1aa',
                fontSize: '18px',
                cursor: 'pointer',
                padding: '4px',
                lineHeight: 1
              }}
            >
              ✕
            </button>
          </div>

          {/* Modal Body */}
          <div style={{ padding: '20px', display: 'flex', flexDirection: 'column', gap: '16px' }}>
            {/* User & Role Badge */}
            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              backgroundColor: '#27272a',
              padding: '10px 14px',
              borderRadius: '8px',
              fontSize: '12px'
            }}>
              <span style={{ color: '#d4d4d8' }}>
                User: <strong style={{ color: '#ffffff' }}>{user?.email}</strong>
              </span>
              <span style={{
                backgroundColor: 'rgba(59, 130, 246, 0.2)',
                color: '#93c5fd',
                padding: '2px 8px',
                borderRadius: '4px',
                fontWeight: '600',
                fontSize: '11px',
                textTransform: 'uppercase',
                letterSpacing: '0.3px'
              }}>
                {user?.role?.replace('_', ' ')}
              </span>
            </div>

            {/* Status Alert */}
            {tokenCopied && (
              <div style={{
                backgroundColor: 'rgba(34, 197, 94, 0.15)',
                border: '1px solid rgba(34, 197, 94, 0.3)',
                color: '#4ade80',
                padding: '8px 12px',
                borderRadius: '6px',
                fontSize: '12px',
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}>
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="20 6 9 17 4 12" />
                </svg>
                <span>Bearer token copied to clipboard!</span>
              </div>
            )}

            {/* Token Display */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
              <label style={{ fontSize: '11px', fontWeight: '700', color: '#a1a1aa', textTransform: 'uppercase' }}>
                Bearer JWT Token
              </label>
              <textarea
                data-testid="bearer-token-textarea"
                readOnly
                rows={4}
                value={sessionStorage.getItem('token') || localStorage.getItem('token') || ''}
                onClick={(e) => e.target.select()}
                style={{
                  backgroundColor: '#111113',
                  border: '1px solid #3f3f46',
                  borderRadius: '8px',
                  padding: '10px 12px',
                  color: '#e4e4e7',
                  fontSize: '12px',
                  fontFamily: 'SFMono-Regular, Menlo, Monaco, Consolas, monospace',
                  resize: 'none',
                  lineHeight: '1.4',
                  wordBreak: 'break-all'
                }}
              />
            </div>

            {/* Usage Hint */}
            <div style={{
              backgroundColor: '#222226',
              border: '1px solid #333338',
              borderRadius: '8px',
              padding: '10px 12px',
              fontSize: '11px',
              color: '#a1a1aa',
              lineHeight: '1.5'
            }}>
              <div style={{ fontWeight: '600', color: '#d4d4d8', marginBottom: '4px' }}>
                💡 How to use:
              </div>
              <div>• Paste into <strong>Destination Auth Token</strong> when pushing Card or Coin Face templates to another server.</div>
              <div>• Use in cURL / API headers: <code style={{ color: '#93c5fd', background: '#18181b', padding: '1px 5px', borderRadius: '3px' }}>Authorization: Bearer &lt;token&gt;</code></div>
            </div>
          </div>

          {/* Modal Footer */}
          <div style={{
            display: 'flex',
            justifyContent: 'flex-end',
            gap: '10px',
            padding: '12px 20px',
            borderTop: '1px solid #27272a',
            backgroundColor: '#1f1f23'
          }}>
            <button
              type="button"
              data-testid="close-bearer-token-modal-footer-btn"
              onClick={() => setShowTokenModal(false)}
              style={{
                backgroundColor: '#27272a',
                color: '#e4e4e7',
                border: '1px solid #3f3f46',
                borderRadius: '6px',
                padding: '8px 16px',
                fontSize: '12px',
                fontWeight: '500',
                cursor: 'pointer'
              }}
            >
              Close
            </button>
            <button
              type="button"
              data-testid="copy-bearer-token-modal-btn"
              onClick={() => {
                const activeToken = sessionStorage.getItem('token') || localStorage.getItem('token') || '';
                copyTextToClipboard(activeToken);
              }}
              style={{
                backgroundColor: tokenCopied ? '#16a34a' : 'var(--primary-color, #3b82f6)',
                color: '#ffffff',
                border: 'none',
                borderRadius: '6px',
                padding: '8px 18px',
                fontSize: '12px',
                fontWeight: '600',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                transition: 'background-color 0.2s'
              }}
            >
              {tokenCopied ? (
                <>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="20 6 9 17 4 12" />
                  </svg>
                  Copied!
                </>
              ) : (
                <>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
                    <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
                  </svg>
                  Copy Token
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    )}
  </>
  );
}
